import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, LogOut, Save, X, Download, Check, CalendarDays, Clock, Euro,
  Inbox, BarChart3, Mail, Phone, Users, Eye, MousePointerClick, ChevronDown, ChevronUp, Send } from "lucide-react";
import { useAuth } from "@/lib/AuthContext";
import { getList, createItem, updateItem, deleteItem, getSettings, saveSettings, API,
  getAppointments, updateAppointment, createAppointment, deleteAppointment,
  getBookingStats, getBookingSettings, saveBookingSettings,
  getRequests, updateRequest, deleteRequest, getAnalytics } from "@/lib/api";

const CATEGORIES = ["Ésotérisme", "Médiumnité", "Développement personnel", "Deuil", "Poésie", "Autobiographie", "Fiction / fantasy", "Feel good", "Érotisme", "Spiritualité"];
const EVENT_CATS = ["conférence", "salon", "consultation", "rencontre", "dédicace", "AAESAD", "événement", "atelier"];
const VIDEO_CATS = ["médiumnité", "histoire", "ésotérisme", "deuil", "anecdote historique", "rêves", "réflexions", "conférences"];

const SCHEMAS = {
  books: [
    { k: "title", label: "Titre", type: "text" },
    { k: "genre", label: "Genre / catégorie", type: "select", options: CATEGORIES },
    { k: "price", label: "Prix", type: "text" },
    { k: "pages", label: "Nombre de pages", type: "text" },
    { k: "format", label: "Format", type: "text" },
    { k: "cover_url", label: "URL couverture", type: "text" },
    { k: "summary_short", label: "Résumé court", type: "textarea" },
    { k: "summary_long", label: "Présentation / résumé long", type: "textarea" },
    { k: "excerpt", label: "Extrait", type: "textarea" },
    { k: "audience", label: "Pour qui est ce livre ?", type: "textarea" },
    { k: "themes", label: "Thèmes (séparés par des virgules)", type: "list" },
    { k: "buy_links.fnac", label: "Lien Fnac", type: "text" },
    { k: "buy_links.cultura", label: "Lien Cultura", type: "text" },
    { k: "buy_links.amazon", label: "Lien Amazon", type: "text" },
    { k: "buy_links.bookelis", label: "Lien Bookelis", type: "text" },
    { k: "is_new", label: "Nouveauté / à paraître", type: "bool" },
    { k: "sort_order", label: "Ordre d'affichage", type: "number" },
  ],
  events: [
    { k: "title", label: "Titre", type: "text" },
    { k: "date", label: "Date", type: "date" },
    { k: "time", label: "Heure", type: "text" },
    { k: "location", label: "Lieu", type: "text" },
    { k: "category", label: "Catégorie", type: "select", options: EVENT_CATS },
    { k: "price", label: "Tarif", type: "text" },
    { k: "image_url", label: "URL image", type: "text" },
    { k: "link", label: "Lien", type: "text" },
    { k: "description", label: "Description", type: "textarea" },
  ],
  conferences: [
    { k: "title", label: "Titre", type: "text" },
    { k: "type", label: "Type", type: "select", options: ["upcoming", "past"] },
    { k: "date", label: "Date", type: "text" },
    { k: "location", label: "Lieu", type: "text" },
    { k: "image_url", label: "URL image", type: "text" },
    { k: "link", label: "Lien", type: "text" },
    { k: "description", label: "Description", type: "textarea" },
    { k: "sort_order", label: "Ordre", type: "number" },
  ],
  press: [
    { k: "title", label: "Titre", type: "text" },
    { k: "media", label: "Média", type: "text" },
    { k: "date", label: "Date", type: "text" },
    { k: "image_url", label: "URL image", type: "text" },
    { k: "link", label: "Lien", type: "text" },
    { k: "excerpt", label: "Extrait", type: "textarea" },
  ],
  projects: [
    { k: "title", label: "Titre", type: "text" },
    { k: "status", label: "Statut", type: "select", options: ["en_cours", "a_venir", "termine"] },
    { k: "image_url", label: "URL image", type: "text" },
    { k: "link", label: "Lien", type: "text" },
    { k: "description", label: "Description", type: "textarea" },
    { k: "sort_order", label: "Ordre", type: "number" },
  ],
  podcasts: [
    { k: "title", label: "Titre", type: "text" },
    { k: "date", label: "Date", type: "text" },
    { k: "image_url", label: "URL image", type: "text" },
    { k: "audio_url", label: "URL audio", type: "text" },
    { k: "link", label: "Lien plateforme", type: "text" },
    { k: "description", label: "Description", type: "textarea" },
  ],
  videos: [
    { k: "title", label: "Titre", type: "text" },
    { k: "platform", label: "Plateforme", type: "select", options: ["youtube", "tiktok", "instagram", "autre"] },
    { k: "category", label: "Catégorie", type: "select", options: VIDEO_CATS },
    { k: "thumbnail_url", label: "URL miniature", type: "text" },
    { k: "duration", label: "Durée (ex : 12:30)", type: "text" },
    { k: "link", label: "Lien (YouTube / TikTok / autre)", type: "text" },
    { k: "sort_order", label: "Ordre", type: "number" },
  ],
};

const TABS = [
  ["rendezvous", "Rendez-vous"],
  ["requests", "Demandes"], ["stats", "Statistiques"],
  ["books", "Livres"], ["events", "Agenda"], ["conferences", "Conférences"],
  ["press", "Presse"], ["projects", "Projets"], ["podcasts", "Podcast"],
  ["videos", "Vidéos"], ["settings", "Réglages"],
];

const RDV_STATUS = { en_attente: "En attente", confirme: "Confirmé", refuse: "Refusé", paye: "Payé", honore: "Honoré", annule: "Annulé" };
const RDV_COLOR = { en_attente: "#D4B978", confirme: "#8Fc98e", refuse: "#d98a8a", paye: "#7ab6d6", honore: "#b39ddb", annule: "#8a8a8a" };
const RDV_MODE = { visio: "Visio", telephone: "Téléphone", bureau: "Bureau" };
const WDAYS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

function RendezVousManager() {
  const qc = useQueryClient();
  const { data: appts = [] } = useQuery({ queryKey: ["appointments"], queryFn: getAppointments });
  const { data: stats } = useQuery({ queryKey: ["booking-stats"], queryFn: getBookingStats });
  const { data: bset } = useQuery({ queryKey: ["booking-settings"], queryFn: getBookingSettings });
  const [filter, setFilter] = useState("all");
  const [openId, setOpenId] = useState(null);
  const [adding, setAdding] = useState(false);
  const [cfg, setCfg] = useState(null);
  const base = "w-full bg-[#050814] border border-[rgba(198,167,106,0.3)] px-3 py-2 text-[#F3E9D2] text-sm focus:outline-none focus:border-[#C6A76A]";

  const refresh = () => { qc.invalidateQueries({ queryKey: ["appointments"] }); qc.invalidateQueries({ queryKey: ["booking-stats"] }); };
  const patch = async (id, data) => {
    try {
      const result = await updateAppointment(id, data); refresh();
      if (data.status === "confirme" && result.notifications?.confirmed !== "envoyé") {
        toast.warning("Demande acceptée, mais l’e-mail de confirmation n’a pas été envoyé. Contactez personnellement le client.");
      } else toast.success("Mis à jour");
    } catch (error) { toast.error(error.response?.data?.detail || "Mise à jour impossible"); }
  };
  const remove = async (id) => { if (!window.confirm("Supprimer ce rendez-vous ?")) return; await deleteAppointment(id); refresh(); };

  const filtered = appts.filter((a) => (filter === "all" ? true : a.status === filter));
  const groups = {};
  filtered.forEach((a) => { (groups[a.date] = groups[a.date] || []).push(a); });
  const dates = Object.keys(groups).sort();

  const cards = [
    ["Demandes", stats?.total ?? 0], ["En attente", stats?.by_status?.en_attente ?? 0],
    ["Validés", stats?.validated ?? 0], ["Payés", stats?.paid ?? 0],
    ["Conversion", `${stats?.conversion ?? 0} %`], ["Échanges évités", stats?.exchanges_saved ?? 0],
  ];

  const saveCfg = async () => { try { await saveBookingSettings(cfg); qc.invalidateQueries({ queryKey: ["booking-settings"] }); toast.success("Disponibilités enregistrées"); setCfg(null); } catch { toast.error("Erreur"); } };
  const toggleDay = (d) => setCfg((c) => ({ ...c, work_days: c.work_days.includes(d) ? c.work_days.filter((x) => x !== d) : [...c.work_days, d].sort() }));

  const [form, setForm] = useState({ prestation: "", mode: "bureau", date: "", time: "", name: "", email: "", phone: "", price_base: 0 });
  const submitManual = async () => {
    if (!form.prestation || !form.date || !form.time) { toast.error("Prestation, date et heure requises."); return; }
    try { await createAppointment({ ...form, surcharge: 0, status: "confirme", payment_status: "non_paye" }); setAdding(false); setForm({ prestation: "", mode: "bureau", date: "", time: "", name: "", email: "", phone: "", price_base: 0 }); refresh(); toast.success("Rendez-vous ajouté"); }
    catch { toast.error("Erreur"); }
  };

  return (
    <div data-testid="admin-rdv-manager">
      {/* Compteur preuve concours */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
        {cards.map(([label, val]) => (
          <div key={label} className="border border-[rgba(198,167,106,0.25)] bg-[#0B1020] p-4">
            <div className="serif text-2xl text-[#D4B978]">{val}</div>
            <div className="text-[0.6rem] uppercase tracking-widest text-[#F3E9D2]/50 mt-1">{label}</div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-6">
        {[["all", "Tous"], ["en_attente", "En attente"], ["confirme", "Confirmés"], ["paye", "Payés"], ["honore", "Honorés"], ["refuse", "Refusés"], ["annule", "Annulés"]].map(([k, l]) => (
          <button key={k} data-testid={`rdv-filter-${k}`} onClick={() => setFilter(k)} className={`px-3 py-1.5 text-xs border ${filter === k ? "bg-[#C6A76A] text-[#050814] border-[#C6A76A]" : "border-[rgba(198,167,106,0.3)] text-[#F3E9D2]/70"}`}>{l}</button>
        ))}
        <div className="flex-1" />
        <button onClick={() => setAdding((v) => !v)} className="dl-btn text-xs inline-flex items-center gap-1"><Plus size={14} /> Ajout manuel</button>
        <button onClick={() => setCfg(bset || { work_days: [1, 2, 3, 4, 5], work_start: "10:00", work_end: "19:00", slot_minutes: 60, blocked_dates: [], paypal_me: "", iban: "", wero: "" })} className="dl-btn text-xs inline-flex items-center gap-1"><CalendarDays size={14} /> Disponibilités</button>
      </div>

      {adding && (
        <div className="border border-[rgba(198,167,106,0.3)] bg-[#0B1020] p-5 mb-6 grid grid-cols-2 md:grid-cols-4 gap-3">
          <input className={base} placeholder="Prestation" value={form.prestation} onChange={(e) => setForm({ ...form, prestation: e.target.value })} />
          <select className={base} value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })}>{Object.entries(RDV_MODE).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
          <input className={base} type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
          <input className={base} placeholder="Heure 14:00" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} />
          <input className={base} placeholder="Nom" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <input className={base} placeholder="E-mail" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <input className={base} placeholder="Téléphone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          <input className={base} type="number" placeholder="Tarif €" value={form.price_base} onChange={(e) => setForm({ ...form, price_base: Number(e.target.value) })} />
          <button onClick={submitManual} className="dl-btn dl-btn-solid text-xs col-span-2 md:col-span-1"><Save size={14} /> Enregistrer</button>
        </div>
      )}

      {cfg && (
        <div className="border border-[rgba(198,167,106,0.3)] bg-[#0B1020] p-5 mb-6 space-y-4">
          <div className="flex items-center justify-between"><span className="dl-eyebrow">Mes disponibilités</span><button onClick={() => setCfg(null)}><X size={16} className="text-[#F3E9D2]/60" /></button></div>
          <div className="flex flex-wrap gap-2">{WDAYS.map((d, i) => (<button key={d} onClick={() => toggleDay(i)} className={`px-3 py-1.5 text-xs border ${cfg.work_days.includes(i) ? "bg-[#C6A76A] text-[#050814] border-[#C6A76A]" : "border-[rgba(198,167,106,0.3)] text-[#F3E9D2]/70"}`}>{d}</button>))}</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <label className="text-xs text-[#F3E9D2]/60">Début<input className={base} value={cfg.work_start} onChange={(e) => setCfg({ ...cfg, work_start: e.target.value })} /></label>
            <label className="text-xs text-[#F3E9D2]/60">Fin<input className={base} value={cfg.work_end} onChange={(e) => setCfg({ ...cfg, work_end: e.target.value })} /></label>
            <label className="text-xs text-[#F3E9D2]/60">Durée créneau (min)<input className={base} type="number" value={cfg.slot_minutes} onChange={(e) => setCfg({ ...cfg, slot_minutes: Number(e.target.value) })} /></label>
            <label className="text-xs text-[#F3E9D2]/60">Jours bloqués (AAAA-MM-JJ, séparés par ,)<input className={base} value={(cfg.blocked_dates || []).join(", ")} onChange={(e) => setCfg({ ...cfg, blocked_dates: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} /></label>
            <label className="text-xs text-[#F3E9D2]/60">PayPal.me<input className={base} value={cfg.paypal_me || ""} onChange={(e) => setCfg({ ...cfg, paypal_me: e.target.value })} /></label>
            <label className="text-xs text-[#F3E9D2]/60">IBAN (virement)<input className={base} value={cfg.iban || ""} onChange={(e) => setCfg({ ...cfg, iban: e.target.value })} /></label>
            <label className="text-xs text-[#F3E9D2]/60">Wero<input className={base} value={cfg.wero || ""} onChange={(e) => setCfg({ ...cfg, wero: e.target.value })} /></label>
          </div>
          <button onClick={saveCfg} className="dl-btn dl-btn-solid text-xs"><Save size={14} /> Enregistrer</button>
        </div>
      )}

      {dates.length === 0 ? (
        <p className="text-[#F3E9D2]/50 italic py-10 text-center">Aucun rendez-vous {filter !== "all" ? RDV_STATUS[filter]?.toLowerCase() : ""} pour le moment.</p>
      ) : dates.map((date) => (
        <div key={date} className="mb-6">
          <div className="dl-eyebrow mb-2 flex items-center gap-2"><CalendarDays size={13} /> {date}</div>
          <div className="space-y-2">
            {groups[date].sort((a, b) => (a.time || "").localeCompare(b.time || "")).map((a) => (
              <div key={a.id} data-testid={`rdv-row-${a.id}`} className="border border-[rgba(198,167,106,0.2)] bg-[#0B1020]">
                <div className="flex flex-wrap items-center gap-3 p-3">
                  <span className="inline-flex items-center gap-1 text-[#F3E9D2]/90 text-sm"><Clock size={13} className="text-[#C6A76A]" /> {a.time}</span>
                  <span className="text-[#F3E9D2] text-sm font-medium">{a.name || "—"}</span>
                  <span className="text-[#F3E9D2]/60 text-xs">{a.prestation} · {RDV_MODE[a.mode] || a.mode}</span>
                  <span className="text-[#D4B978] text-sm inline-flex items-center gap-0.5"><Euro size={12} />{a.total}</span>
                  <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 border" style={{ color: RDV_COLOR[a.status], borderColor: RDV_COLOR[a.status] }}>{RDV_STATUS[a.status]}</span>
                  {a.payment_status === "paye" && <span className="text-[10px] uppercase tracking-widest text-[#8Fc98e]">payé</span>}
                  <div className="flex-1" />
                  <button onClick={() => setOpenId(openId === a.id ? null : a.id)} className="text-xs dl-link">{openId === a.id ? "Fermer" : "Détails"}</button>
                </div>
                {openId === a.id && (
                  <div className="border-t border-[rgba(198,167,106,0.15)] p-3 text-sm text-[#F3E9D2]/75 space-y-3">
                    <div className="grid grid-cols-2 gap-y-1 text-xs">
                      <span className="text-[#F3E9D2]/45">E-mail</span><span>{a.email}</span>
                      <span className="text-[#F3E9D2]/45">Téléphone</span><span>{a.phone || "—"}</span>
                      <span className="text-[#F3E9D2]/45">Tarif base / supplément</span><span>{a.price_base} € {a.surcharge ? `(+${a.surcharge} €)` : ""}</span>
                      {a.message && (<><span className="text-[#F3E9D2]/45">Message</span><span>{a.message}</span></>)}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button data-testid={`rdv-validate-${a.id}`} onClick={() => patch(a.id, { status: "confirme", _action: "Accepté personnellement par Dorothée Lancelot" })} className="dl-btn dl-btn-solid text-xs"><Check size={13} /> Accepter et confirmer</button>
                      <button onClick={() => patch(a.id, { status: "refuse", _action: "Refusé" })} className="dl-btn text-xs">Refuser</button>
                      <button data-testid={`rdv-paid-${a.id}`} onClick={() => patch(a.id, { status: "paye", payment_status: "paye", _action: "Paiement reçu" })} className="dl-btn text-xs">Marquer payé</button>
                      <button onClick={() => patch(a.id, { status: "honore", _action: "Honoré" })} className="dl-btn text-xs">Honoré</button>
                      <button onClick={() => patch(a.id, { status: "annule", _action: "Annulé" })} className="dl-btn text-xs">Annuler</button>
                      <button onClick={() => remove(a.id)} className="text-xs text-[#d98a8a] inline-flex items-center gap-1"><Trash2 size={13} /> Supprimer</button>
                    </div>
                    {a.notifications && <p className="text-xs" role="status">Notifications : accusé de réception {a.notifications.receipt || "non envoyé"} ; demande à Dorothée {a.notifications.owner || "non envoyée"} ; confirmation {a.notifications.confirmed || "non envoyée"}.</p>}
                    <div className="flex flex-wrap items-end gap-2 pt-1">
                      <label className="text-[10px] uppercase tracking-widest text-[#F3E9D2]/45">Reporter — date<input type="date" defaultValue={a.date} className={base} onChange={(e) => (a._nd = e.target.value)} /></label>
                      <label className="text-[10px] uppercase tracking-widest text-[#F3E9D2]/45">heure<input defaultValue={a.time} className={base} onChange={(e) => (a._nt = e.target.value)} /></label>
                      <button onClick={() => patch(a.id, { date: a._nd || a.date, time: a._nt || a.time, _action: "Créneau proposé/modifié" })} className="dl-btn text-xs">Proposer ce créneau</button>
                    </div>
                    {a.payment_method && <div className="text-xs text-[#F3E9D2]/45">Paiement : {a.payment_method}</div>}
                    {(a.history || []).length > 0 && (
                      <div className="text-[11px] text-[#F3E9D2]/40 border-t border-[rgba(198,167,106,0.1)] pt-2">
                        {a.history.map((h, i) => <div key={i}>{(h.at || "").slice(0, 16).replace("T", " ")} — {h.action}</div>)}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

const getVal = (obj, key) => key.split(".").reduce((o, k) => (o ? o[k] : undefined), obj);
const setVal = (obj, key, value) => {
  const parts = key.split("."); const copy = { ...obj }; let cur = copy;
  for (let i = 0; i < parts.length - 1; i++) { cur[parts[i]] = { ...(cur[parts[i]] || {}) }; cur = cur[parts[i]]; }
  cur[parts[parts.length - 1]] = value; return copy;
};

function Field({ field, value, onChange }) {
  const base = "w-full bg-[#050814] border border-[rgba(198,167,106,0.3)] px-3 py-2 text-[#F3E9D2] text-sm focus:outline-none focus:border-[#C6A76A]";
  if (field.type === "textarea")
    return <textarea rows={3} className={base} value={value || ""} onChange={(e) => onChange(e.target.value)} />;
  if (field.type === "select")
    return (
      <select className={base} value={value || ""} onChange={(e) => onChange(e.target.value)}>
        <option value="">—</option>
        {field.options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  if (field.type === "bool")
    return (
      <button type="button" onClick={() => onChange(!value)} className={`px-4 py-2 text-xs border ${value ? "bg-[#C6A76A] text-[#050814] border-[#C6A76A]" : "border-[rgba(198,167,106,0.3)] text-[#F3E9D2]/70"}`}>
        {value ? "Oui" : "Non"}
      </button>
    );
  if (field.type === "list")
    return <input className={base} value={Array.isArray(value) ? value.join(", ") : (value || "")} onChange={(e) => onChange(e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} />;
  return <input type={field.type === "date" ? "date" : field.type === "number" ? "number" : "text"} className={base} value={value ?? ""} onChange={(e) => onChange(field.type === "number" ? Number(e.target.value) : e.target.value)} />;
}

function CollectionManager({ coll }) {
  const qc = useQueryClient();
  const { data: items = [] } = useQuery({ queryKey: [coll], queryFn: () => getList(coll) });
  const [editing, setEditing] = useState(null);

  const schema = SCHEMAS[coll];
  const save = async () => {
    try {
      if (editing.id) await updateItem(coll, editing.id, editing);
      else await createItem(coll, editing);
      toast.success("Enregistré");
      qc.invalidateQueries({ queryKey: [coll] });
      setEditing(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Supprimer cet élément ?")) return;
    try { await deleteItem(coll, id); toast.success("Supprimé"); qc.invalidateQueries({ queryKey: [coll] }); }
    catch { toast.error("Erreur"); }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <p className="text-[#F3E9D2]/60 text-sm">{items.length} élément(s)</p>
        <button data-testid={`admin-add-${coll}`} onClick={() => setEditing({})} className="dl-btn dl-btn-solid" style={{ padding: "0.6rem 1.3rem", fontSize: "0.7rem" }}><Plus size={15} /> Ajouter</button>
      </div>

      {editing && (
        <div className="border border-[rgba(198,167,106,0.35)] bg-[#0B1020] p-6 mb-8">
          <div className="flex justify-between mb-5">
            <h3 className="serif text-2xl text-[#F3E9D2]">{editing.id ? "Modifier" : "Nouvel élément"}</h3>
            <button onClick={() => setEditing(null)} className="text-[#F3E9D2]/60 hover:text-[#F3E9D2]"><X size={20} /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {schema.map((f) => (
              <div key={f.k} className={f.type === "textarea" ? "md:col-span-2" : ""}>
                <label className="dl-eyebrow block mb-1.5" style={{ fontSize: "0.55rem" }}>{f.label}</label>
                <Field field={f} value={getVal(editing, f.k)} onChange={(v) => setEditing((cur) => setVal(cur, f.k, v))} />
              </div>
            ))}
          </div>
          <button data-testid="admin-save-btn" onClick={save} className="dl-btn dl-btn-solid mt-6"><Save size={15} /> Enregistrer</button>
        </div>
      )}

      <div className="space-y-2">
        {items.map((it) => (
          <div key={it.id} className="flex items-center justify-between border border-[rgba(198,167,106,0.15)] bg-[#0B1020] px-5 py-3">
            <span className="text-[#F3E9D2]/90">{it.title || it.id}</span>
            <div className="flex gap-2">
              <button data-testid={`admin-edit-${it.id}`} onClick={() => setEditing(it)} className="p-2 text-[#C6A76A] hover:bg-white/5"><Pencil size={16} /></button>
              <button data-testid={`admin-del-${it.id}`} onClick={() => remove(it.id)} className="p-2 text-[#e0a0a0] hover:bg-white/5"><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SettingsManager() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["settings"], queryFn: getSettings });
  const [form, setForm] = useState(null);
  useEffect(() => { if (data && !form) setForm(data); }, [data]); // eslint-disable-line

  if (!form) return null;
  const up = (k, v) => setForm((c) => setVal(c, k, v));
  const save = async () => {
    try { await saveSettings(form); toast.success("Réglages enregistrés"); qc.invalidateQueries({ queryKey: ["settings"] }); }
    catch { toast.error("Erreur"); }
  };
  const inp = "w-full bg-[#050814] border border-[rgba(198,167,106,0.3)] px-3 py-2 text-[#F3E9D2] text-sm focus:outline-none focus:border-[#C6A76A]";
  const nets = ["facebook", "instagram", "tiktok", "youtube"];

  return (
    <div className="space-y-8 max-w-3xl">
      <div>
        <h3 className="serif text-2xl text-[#F3E9D2] mb-4">Textes & informations</h3>
        <label className="dl-eyebrow block mb-1.5" style={{ fontSize: "0.55rem" }}>Texte d'introduction (accueil)</label>
        <textarea rows={4} className={inp} value={form.hero_intro || ""} onChange={(e) => up("hero_intro", e.target.value)} />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <div><label className="dl-eyebrow block mb-1.5" style={{ fontSize: "0.55rem" }}>SIRET professionnel</label><input className={inp} value={form.siret_pro || ""} onChange={(e) => up("siret_pro", e.target.value)} /></div>
          <div><label className="dl-eyebrow block mb-1.5" style={{ fontSize: "0.55rem" }}>SIRET AAESAD</label><input className={inp} value={form.siret_aaesad || ""} onChange={(e) => up("siret_aaesad", e.target.value)} /></div>
          <div><label className="dl-eyebrow block mb-1.5" style={{ fontSize: "0.55rem" }}>Téléphone rendez-vous</label><input className={inp} value={form.appointment_phone || ""} onChange={(e) => up("appointment_phone", e.target.value)} placeholder="Ex : 06 XX XX XX XX" /></div>
          <div><label className="dl-eyebrow block mb-1.5" style={{ fontSize: "0.55rem" }}>E-mail rendez-vous</label><input className={inp} value={form.appointment_email || "dorothee-medium@hotmail.fr"} onChange={(e) => up("appointment_email", e.target.value)} /></div>
          <div><label className="dl-eyebrow block mb-1.5" style={{ fontSize: "0.55rem" }}>Acompte</label><input className={inp} value={form.acompte || ""} onChange={(e) => up("acompte", e.target.value)} /></div>
          <div><label className="dl-eyebrow block mb-1.5" style={{ fontSize: "0.55rem" }}>Tarif hypnose</label><input className={inp} value={form.hypnose_tarif || ""} onChange={(e) => up("hypnose_tarif", e.target.value)} /></div>
          <div><label className="dl-eyebrow block mb-1.5" style={{ fontSize: "0.55rem" }}>URL Chroniques</label><input className={inp} value={form.chroniques_url || ""} onChange={(e) => up("chroniques_url", e.target.value)} /></div>
          <div><label className="dl-eyebrow block mb-1.5" style={{ fontSize: "0.55rem" }}>URL AAESAD</label><input className={inp} value={form.aaesad_url || ""} onChange={(e) => up("aaesad_url", e.target.value)} /></div>
        </div>
      </div>

      <div>
        <h3 className="serif text-2xl text-[#F3E9D2] mb-4">Réseaux sociaux</h3>
        <div className="space-y-3">
          {nets.map((n) => (
            <div key={n} className="grid grid-cols-1 md:grid-cols-[110px_1fr_1fr_150px] gap-3 items-center">
              <span className="capitalize text-[#F3E9D2]/80 text-sm">{n}</span>
              <input className={inp} placeholder="Nom affiché" value={form.social?.[n]?.name || ""} onChange={(e) => up(`social.${n}.name`, e.target.value)} />
              <input className={inp} placeholder="URL" value={form.social?.[n]?.url || ""} onChange={(e) => up(`social.${n}.url`, e.target.value)} />
              <input className={inp} placeholder="Abonnés" value={form.social?.[n]?.count || ""} onChange={(e) => up(`social.${n}.count`, e.target.value)} />
            </div>
          ))}
        </div>
      </div>

      <button data-testid="admin-save-settings" onClick={save} className="dl-btn dl-btn-solid"><Save size={15} /> Enregistrer les réglages</button>
    </div>
  );
}

const REQ_TYPE = { consultation: "Consultation", conference: "Conférence", presse: "Presse", partenariat: "Partenariat", evenement: "Événement", livre: "Livre", autre: "Autre" };
const REQ_STATUS = { nouveau: "Nouveau", en_cours: "En cours", traite: "Traité", archive: "Archivé" };
const REQ_STATUS_COLOR = { nouveau: "#D4B978", en_cours: "#7ab6d6", traite: "#8Fc98e", archive: "#8a8a8a" };

function fmtDate(iso) {
  try { return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso || ""; }
}

function RequestsManager() {
  const qc = useQueryClient();
  const { data: reqs = [], isLoading } = useQuery({ queryKey: ["requests"], queryFn: getRequests });
  const [filter, setFilter] = useState("all");
  const [openId, setOpenId] = useState(null);

  const counts = useMemo(() => {
    const c = { all: reqs.length, nouveau: 0, en_cours: 0, traite: 0, archive: 0 };
    reqs.forEach((r) => { const s = r.status || "nouveau"; c[s] = (c[s] || 0) + 1; });
    return c;
  }, [reqs]);
  const filtered = filter === "all" ? reqs : reqs.filter((r) => (r.status || "nouveau") === filter);

  const setStatus = async (id, status) => {
    try { await updateRequest(id, { status }); toast.success("Statut mis à jour"); qc.invalidateQueries({ queryKey: ["requests"] }); }
    catch { toast.error("Erreur"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Supprimer cette demande ?")) return;
    try { await deleteRequest(id); toast.success("Supprimé"); setOpenId(null); qc.invalidateQueries({ queryKey: ["requests"] }); }
    catch { toast.error("Erreur"); }
  };

  const FILTERS = [["all", "Toutes"], ["nouveau", "Nouveau"], ["en_cours", "En cours"], ["traite", "Traité"], ["archive", "Archivé"]];

  return (
    <div data-testid="admin-requests">
      <div className="flex flex-wrap gap-2 mb-8">
        {FILTERS.map(([k, l]) => (
          <button key={k} data-testid={`req-filter-${k}`} onClick={() => setFilter(k)}
            className={`px-4 py-2 text-[0.65rem] tracking-widest uppercase border transition-colors ${filter === k ? "bg-[#C6A76A] text-[#050814] border-[#C6A76A]" : "border-[rgba(198,167,106,0.3)] text-[#F3E9D2]/70 hover:border-[#C6A76A]"}`}>
            {l} <span className="opacity-60">· {counts[k] || 0}</span>
          </button>
        ))}
      </div>

      {isLoading && <p className="text-[#F3E9D2]/50 text-sm">Chargement…</p>}
      {!isLoading && filtered.length === 0 && (
        <div className="border border-[rgba(198,167,106,0.15)] bg-[#0B1020] px-6 py-10 text-center">
          <Inbox size={26} className="text-[#C6A76A] mx-auto mb-3" />
          <p className="text-[#F3E9D2]/60 text-sm">Aucune demande {filter !== "all" ? `« ${REQ_STATUS[filter]} »` : ""} pour l'instant.</p>
        </div>
      )}

      <div className="space-y-2">
        {filtered.map((r) => {
          const st = r.status || "nouveau";
          const open = openId === r.id;
          return (
            <div key={r.id} data-testid={`req-row-${r.id}`} className="border border-[rgba(198,167,106,0.15)] bg-[#0B1020]">
              <button onClick={() => setOpenId(open ? null : r.id)} className="w-full flex items-center gap-3 px-5 py-3.5 text-left">
                <span className="text-[0.6rem] tracking-widest uppercase px-2 py-1 border" style={{ color: "#D4B978", borderColor: "rgba(198,167,106,0.35)" }}>{REQ_TYPE[r.type] || r.type}</span>
                <div className="min-w-0 flex-1">
                  <div className="text-[#F3E9D2]/90 text-sm truncate">{r.name || r.email || "Sans nom"}</div>
                  <div className="text-[#F3E9D2]/45 text-xs truncate">{r.email}{r.phone ? ` · ${r.phone}` : ""}</div>
                </div>
                <span className="hidden sm:inline text-[#F3E9D2]/40 text-xs">{fmtDate(r.created_at)}</span>
                <span className="text-[0.58rem] tracking-widest uppercase px-2 py-1 rounded" style={{ color: REQ_STATUS_COLOR[st], border: `1px solid ${REQ_STATUS_COLOR[st]}55` }}>{REQ_STATUS[st]}</span>
                {open ? <ChevronUp size={16} className="text-[#C6A76A]" /> : <ChevronDown size={16} className="text-[#C6A76A]" />}
              </button>

              {open && (
                <div className="border-t border-[rgba(198,167,106,0.15)] px-5 py-5 space-y-4">
                  {r.message && <p className="text-[#F3E9D2]/80 text-sm whitespace-pre-wrap leading-relaxed bg-[#050814] border border-[rgba(198,167,106,0.15)] p-4">{r.message}</p>}
                  {r.fields && Object.keys(r.fields).length > 0 && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                      {Object.entries(r.fields).map(([k, v]) => (
                        <div key={k} className="text-[#F3E9D2]/70"><span className="text-[#F3E9D2]/45 capitalize">{k} :</span> {v}</div>
                      ))}
                    </div>
                  )}
                  <div className="flex flex-wrap items-center gap-2 pt-1">
                    <a href={`mailto:${r.email}`} data-testid={`req-mail-${r.id}`} className="inline-flex items-center gap-1.5 px-3 py-2 border border-[rgba(198,167,106,0.35)] text-[#D4B978] text-xs hover:bg-white/5"><Mail size={13} /> Répondre</a>
                    {r.phone && <a href={`tel:${r.phone}`} className="inline-flex items-center gap-1.5 px-3 py-2 border border-[rgba(198,167,106,0.35)] text-[#D4B978] text-xs hover:bg-white/5"><Phone size={13} /> Appeler</a>}
                    <span className="mx-1 text-[#F3E9D2]/30">|</span>
                    {Object.entries(REQ_STATUS).map(([k, l]) => (
                      <button key={k} data-testid={`req-status-${r.id}-${k}`} onClick={() => setStatus(r.id, k)}
                        className={`px-3 py-2 text-xs border transition-colors ${st === k ? "bg-[#C6A76A] text-[#050814] border-[#C6A76A]" : "border-[rgba(198,167,106,0.3)] text-[#F3E9D2]/70 hover:border-[#C6A76A]"}`}>{l}</button>
                    ))}
                    <button data-testid={`req-del-${r.id}`} onClick={() => remove(r.id)} className="ml-auto inline-flex items-center gap-1.5 px-3 py-2 text-[#e0a0a0] text-xs hover:bg-white/5"><Trash2 size={13} /> Supprimer</button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const EVENT_LABELS = {
  page_view: "Pages vues", concierge_open: "Ouvertures concierge", concierge_intent: "Intentions choisies",
  rdv_open: "Ouvertures rendez-vous", demande_start: "Formulaires ouverts", demande_submit: "Demandes envoyées",
  assistant_ask: "Questions Dorothée IA", book_click: "Clics livres", event_click: "Clics agenda",
  conference_invite: "Invitations conférence", press_view: "Vues presse", partnership: "Partenariats",
};

function StatCard({ Icon, label, value, tid }) {
  return (
    <div data-testid={tid} className="border border-[rgba(198,167,106,0.2)] bg-[#0B1020] p-5">
      <Icon size={18} className="text-[#C6A76A] mb-3" />
      <div className="serif text-3xl text-[#F3E9D2]">{value}</div>
      <div className="dl-eyebrow mt-1" style={{ fontSize: "0.55rem" }}>{label}</div>
    </div>
  );
}

function StatsManager() {
  const { data: a, isLoading } = useQuery({ queryKey: ["analytics"], queryFn: getAnalytics });
  const { data: b } = useQuery({ queryKey: ["booking-stats"], queryFn: getBookingStats });
  if (isLoading || !a) return <p className="text-[#F3E9D2]/50 text-sm">Chargement…</p>;

  const byName = a.by_name || {};
  const maxDay = Math.max(1, ...(a.days || []).map((d) => d.count));

  return (
    <div data-testid="admin-stats" className="space-y-10">
      <div>
        <h3 className="serif text-2xl text-[#F3E9D2] mb-1">Vue d'ensemble</h3>
        <p className="text-[#F3E9D2]/45 text-sm mb-5">Mesure d'audience anonyme et respectueuse (aucun cookie, aucune donnée personnelle).</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard tid="stat-sessions" Icon={Users} label="Visiteurs (sessions)" value={a.unique_sessions || 0} />
          <StatCard tid="stat-pageviews" Icon={Eye} label="Pages vues" value={byName.page_view || 0} />
          <StatCard tid="stat-events" Icon={MousePointerClick} label="Interactions totales" value={a.total_events || 0} />
          <StatCard tid="stat-ia" Icon={BarChart3} label="Questions Dorothée IA" value={byName.assistant_ask || 0} />
        </div>
      </div>

      <div>
        <h3 className="serif text-2xl text-[#F3E9D2] mb-1">Impact business (preuve concours)</h3>
        <p className="text-[#F3E9D2]/45 text-sm mb-5">Ce que le site génère concrètement.</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard tid="stat-rdv-open" Icon={CalendarDays} label="Ouvertures « Prendre RDV »" value={byName.rdv_open || 0} />
          <StatCard tid="stat-appointments" Icon={Check} label="Demandes de rendez-vous" value={b?.total || 0} />
          <StatCard tid="stat-validated" Icon={Check} label="Rendez-vous validés" value={b?.validated || 0} />
          <StatCard tid="stat-leads" Icon={Inbox} label="Demandes (leads)" value={a.requests_total || 0} />
        </div>
      </div>

      {(a.days || []).length > 0 && (
        <div>
          <h3 className="serif text-2xl text-[#F3E9D2] mb-5">Pages vues — 14 derniers jours</h3>
          <div className="flex items-end gap-1.5 h-40 border-b border-[rgba(198,167,106,0.2)] pb-0">
            {a.days.map((d) => (
              <div key={d.day} className="flex-1 flex flex-col items-center justify-end group" title={`${d.day} : ${d.count}`}>
                <span className="text-[0.6rem] text-[#F3E9D2]/50 mb-1">{d.count}</span>
                <div className="w-full bg-[#C6A76A]/70 group-hover:bg-[#D4B978] transition-colors" style={{ height: `${(d.count / maxDay) * 100}%`, minHeight: 2 }} />
              </div>
            ))}
          </div>
          <div className="flex gap-1.5 mt-1.5">
            {a.days.map((d) => <span key={d.day} className="flex-1 text-center text-[0.55rem] text-[#F3E9D2]/35">{d.day.slice(5)}</span>)}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {(a.top_paths || []).length > 0 && (
          <div>
            <h3 className="serif text-xl text-[#F3E9D2] mb-4">Pages les plus vues</h3>
            <div className="space-y-2">
              {a.top_paths.map((p) => (
                <div key={p.path} className="flex justify-between text-sm border-b border-[rgba(198,167,106,0.12)] pb-2">
                  <span className="text-[#F3E9D2]/75 truncate">{p.path || "/"}</span>
                  <span className="text-[#C6A76A] font-semibold">{p.count}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        <div>
          <h3 className="serif text-xl text-[#F3E9D2] mb-4">Interactions détaillées</h3>
          <div className="space-y-2">
            {Object.entries(byName).sort((x, y) => y[1] - x[1]).map(([k, v]) => (
              <div key={k} className="flex justify-between text-sm border-b border-[rgba(198,167,106,0.12)] pb-2">
                <span className="text-[#F3E9D2]/75">{EVENT_LABELS[k] || k}</span>
                <span className="text-[#C6A76A] font-semibold">{v}</span>
              </div>
            ))}
            {Object.keys(byName).length === 0 && <p className="text-[#F3E9D2]/45 text-sm">Aucune donnée pour l'instant.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("books");

  useEffect(() => {
    if (!loading && (!user || !user.is_admin)) navigate("/admin", { replace: true });
  }, [user, loading, navigate]);

  useEffect(() => { document.title = "Administration — Dorothée Lancelot"; }, []);

  const backendUrl = useMemo(() => API.replace(/\/api$/, ""), []);

  if (loading) return <div className="min-h-screen bg-[#050814] flex items-center justify-center"><p className="dl-eyebrow">Chargement…</p></div>;
  if (!user?.is_admin) return null;

  return (
    <div className="min-h-screen bg-[#050814] dl-noise">
      <header className="border-b border-[rgba(198,167,106,0.2)] bg-[#0B1020] relative z-10">
        <div className="dl-container flex items-center justify-between h-16">
          <span className="serif text-xl tracking-widest text-[#F3E9D2]">ADMINISTRATION</span>
          <div className="flex items-center gap-4">
            <a href={`${API}/export`} target="_blank" rel="noreferrer" className="text-xs text-[#F3E9D2]/60 hover:text-[#D4B978] inline-flex items-center gap-1.5"><Download size={14} /> Export JSON</a>
            <span className="text-xs text-[#F3E9D2]/50 hidden sm:inline">{user.email}</span>
            <button onClick={() => { logout(); navigate("/admin"); }} className="text-xs text-[#F3E9D2]/70 hover:text-[#D4B978] inline-flex items-center gap-1.5"><LogOut size={14} /> Déconnexion</button>
          </div>
        </div>
      </header>

      <div className="dl-container py-10 relative z-10">
        <div className="flex flex-wrap gap-2 mb-10">
          {TABS.map(([k, l]) => (
            <button key={k} data-testid={`admin-tab-${k}`} onClick={() => setTab(k)}
              className={`px-5 py-2.5 text-[0.7rem] tracking-widest uppercase border transition-colors ${tab === k ? "bg-[#C6A76A] text-[#050814] border-[#C6A76A]" : "border-[rgba(198,167,106,0.3)] text-[#F3E9D2]/70"}`}>
              {l}
            </button>
          ))}
        </div>
        {tab === "settings" ? <SettingsManager /> : tab === "rendezvous" ? <RendezVousManager /> : tab === "requests" ? <RequestsManager /> : tab === "stats" ? <StatsManager /> : <CollectionManager coll={tab} key={tab} />}
      </div>
    </div>
  );
}
